import { useReportLoad } from '@hooks/useReportLoad';
import { BaseReport } from '../BaseReport';
import { getUniqueDates, groupByKeys, seriesMap } from '@utils/data';
import { average, countUnique } from '@utils/summaries';
import { basicSort, dateSort } from '@utils/sort';
import { useRange } from '@hooks/useRange';
import { chart } from '@utils/charts';
import { styleDate, styleDateRange } from '@utils/styles';
import { REPORT } from '@consts/reports';

interface AverageCutsPerLocationProps {
    dateRange: { startDate: string; endDate: string };
}

export const AverageCutsPerLocation = ({ dateRange }: AverageCutsPerLocationProps) => {
    const [range, setRange] = useRange('AverageCutsPerLocation');

    const { data, hasData, loading } = useReportLoad({
        reportType: REPORT.AVERAGE_CUTS_PER_LOCATION_PER_COUNTRY_PER_MONTH_COMPARED_TO_REGION_AVERAGE,
        params: { ...dateRange, range },
    });

    const regions = groupByKeys(data, ['regionName', 'date']);

    const countries = groupByKeys(data, ['country', 'date']);

    const dateKeys = getUniqueDates(data, range);

    const regionSeries = seriesMap({
        items: regions,
        dateKeys,
        valueKey: 'regionAvg',
        visible: true,
        name: (region: string) => `Region: ${region}`,
    });

    const countrySeries = seriesMap({
        items: countries,
        dateKeys,
        valueKey: 'countryAvg',
        visible: false,
    });

    return (
        <BaseReport
            title={'Average Cuts per Location'}
            description={
                'This graph shows how many cuts per location each of your countries do, by month, and allows you to compare them to the average of various regions. For example, if you have 5 total locations, and they performed a total of 400 cuts, your average cuts per location would be 80. Only successful, new sale cuts are measured.'
            }
            note={
                'Note: not all of Countries are visible, as some are deselected by default. You can select or deselect Countries to display on the graph by clicking on them in the legend below.'
            }
            rangeText={styleDateRange([dateRange.startDate, dateRange.endDate])}
            loading={loading}
            hasData={hasData}
            range={range}
            setRange={setRange}
            tableOptions={{
                headers: ['Date', 'Country Name', 'Country Avg', 'Region Name', 'Region Avg'],
                data: data.map((item) => [item.date, item.country, item.countryAvg, item.regionName, item.regionAvg]),
                summaries: [countUnique, countUnique, average, countUnique, average],
                style: [styleDate(range)],
                sort: [dateSort(range), basicSort, basicSort, basicSort, basicSort],
            }}
            chartOptions={chart(
                500,
                [
                    { name: 'Country Average', opposite: false },
                    { name: 'Region Average', opposite: true },
                ],
                dateKeys,
                [...regionSeries, ...countrySeries]
            )}
        />
    );
};
